/** Admin → Location Service → Analytics: rural (village / route / demand / delivery cost & route profit) and
 * partner utilisation (orders per hour, average delivery time, per-rider utilisation). Only this store's data. */
import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { ZONE_TYPE_LABEL, inr, useLse } from "../../../lib/lse";
import { Empty, ErrorBox, Loading, PageHead, ResponsiveList, errText, inputCls } from "./LseUI";

const ist = (d) => new Date(d.getTime() + 5.5 * 3600e3).toISOString().slice(0, 10);

export default function LseAnalytics() {
    const { has } = useLse();
    const [tab, setTab] = useState(has("rural_analytics") ? "rural" : "partners");
    const [from, setFrom] = useState(ist(new Date(Date.now() - 29 * 86400e3)));
    const [to, setTo] = useState(ist(new Date()));
    return (
        <div data-testid="lse-analytics">
            <PageHead title="Analytics" sub="Operational numbers for your store only." />
            <div className="mt-3 flex flex-wrap gap-2 items-end">
                {has("rural_analytics") && <button type="button" onClick={() => setTab("rural")} className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${tab === "rural" ? "bg-foreground text-background border-foreground" : "border-border"}`}>Rural & routes</button>}
                {has("partner_analytics") && <button type="button" onClick={() => setTab("partners")} className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${tab === "partners" ? "bg-foreground text-background border-foreground" : "border-border"}`}>Delivery partners</button>}
                <span className="ml-auto inline-flex gap-2 items-center text-xs">
                    <input type="date" className={`${inputCls} !w-auto`} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
                    <span>to</span>
                    <input type="date" className={`${inputCls} !w-auto`} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
                </span>
            </div>
            {tab === "rural" ? <Rural from={from} to={to} /> : <Partners from={from} to={to} />}
        </div>
    );
}

function useReport(path, from, to) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        setData(null); setErr(null);
        api.get(`${path}?date_from=${from}&date_to=${to}`).then(({ data: d }) => setData(d)).catch((e) => setErr(errText(e)));
    }, [path, from, to]);
    return [data, err];
}

function Stat({ label, value }) {
    return <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{label}</p><p className="text-xl font-bold mt-1 tabular-nums">{value}</p></div>;
}

function Bars({ rows, label, value }) {
    const max = Math.max(1, ...rows.map(value));
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-end gap-1 h-32" role="img" aria-label="bar chart">
                {rows.map((r) => (
                    <div key={label(r)} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full" title={`${label(r)}: ${value(r)}`}>
                        <div className="w-full rounded-t bg-primary/80" style={{ height: `${(value(r) / max) * 100}%`, minHeight: value(r) ? 2 : 0 }} />
                        <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">{label(r)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Rural({ from, to }) {
    const [d, err] = useReport("/admin/lse/analytics/rural", from, to);
    if (err) return <ErrorBox message={err} />;
    if (!d) return <Loading />;
    return (
        <div className="mt-4 space-y-5" data-testid="lse-analytics-rural">
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
                <Stat label="Orders" value={d.summary.orders} /><Stat label="Revenue" value={inr(d.summary.revenue)} />
                <Stat label="Average order" value={inr(d.summary.aov)} /><Stat label="Route orders" value={d.summary.route_orders} />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
                <div><h3 className="font-bold text-sm mb-2">Demand by day of week</h3><Bars rows={d.demand_by_weekday} label={(r) => r.day} value={(r) => r.orders} /></div>
                <div><h3 className="font-bold text-sm mb-2">By zone type</h3>
                    <div className="bg-card border border-border rounded-2xl divide-y divide-border">{d.by_zone_type.map((z) => <p key={z.zone_type} className="px-4 py-2 flex justify-between text-sm"><span>{ZONE_TYPE_LABEL[z.zone_type] || "Other / standard"}</span><span className="tabular-nums">{z.orders} · {inr(z.revenue)}</span></p>)}</div>
                </div>
            </div>
            <h3 className="font-bold text-sm">Routes · delivery cost and profit</h3>
            <p className="text-xs text-muted-foreground -mt-4">Profit = platform earnings + delivery fees − (rider earnings + trips × cost per trip).</p>
            {d.routes.length === 0 ? <Empty>No route orders in this period.</Empty> : (
                <ResponsiveList testid="lse-analytics-routes" rows={d.routes} rowKey={(r) => r.route_id}
                    columns={[
                        { key: "route", label: "Route", render: (r) => <span className="font-semibold">{r.route}</span> },
                        { key: "orders", label: "Orders", render: (r) => `${r.orders} (${r.delivered} delivered)` },
                        { key: "trips", label: "Trips", render: (r) => `${r.trips}${r.orders_per_trip ? ` · ${r.orders_per_trip}/trip` : ""}` },
                        { key: "aov", label: "AOV", render: (r) => inr(r.aov) },
                        { key: "cost", label: "Delivery cost", render: (r) => `${inr(r.delivery_cost)}${r.cost_per_order != null ? ` · ${inr(r.cost_per_order)}/order` : ""}` },
                        { key: "profit", label: "Profit", render: (r) => <span className={`font-semibold ${r.profit < 0 ? "text-destructive" : ""}`}>{inr(r.profit)}</span> },
                    ]}
                    card={(r) => (<div><p className="font-semibold">{r.route}</p><p className="text-xs text-muted-foreground">{r.orders} orders · {r.trips} trips · cost {inr(r.delivery_cost)} · profit {inr(r.profit)}</p></div>)} />
            )}
            <h3 className="font-bold text-sm">Villages</h3>
            {d.villages.length === 0 ? <Empty>No village orders in this period.</Empty> : (
                <ResponsiveList testid="lse-analytics-villages" rows={d.villages} rowKey={(r) => r.village_id}
                    columns={[
                        { key: "village", label: "Village", render: (r) => <span className="font-semibold">{r.village}</span> },
                        { key: "orders", label: "Orders" }, { key: "revenue", label: "Revenue", render: (r) => inr(r.revenue) },
                        { key: "aov", label: "AOV", render: (r) => inr(r.aov) }, { key: "customers", label: "Customers" },
                        { key: "repeat", label: "Repeat customers", render: (r) => r.repeat_customers },
                    ]}
                    card={(r) => (<div><p className="font-semibold">{r.village}</p><p className="text-xs text-muted-foreground">{r.orders} orders · {inr(r.revenue)} · {r.customers} customers ({r.repeat_customers} repeat)</p></div>)} />
            )}
        </div>
    );
}

function Partners({ from, to }) {
    const [d, err] = useReport("/admin/lse/analytics/partners", from, to);
    if (err) return <ErrorBox message={err} />;
    if (!d) return <Loading />;
    return (
        <div className="mt-4 space-y-5" data-testid="lse-analytics-partners">
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
                <Stat label="Orders" value={d.summary.orders} /><Stat label="Delivered" value={d.summary.delivered} />
                <Stat label="Avg delivery time" value={d.summary.avg_delivery_minutes != null ? `${d.summary.avg_delivery_minutes} min` : "—"} />
                <Stat label="Busiest hour" value={d.summary.peak_hour != null ? `${d.summary.peak_hour}:00` : "—"} />
            </div>
            <div><h3 className="font-bold text-sm mb-2">Orders per hour (IST)</h3><Bars rows={d.orders_per_hour} label={(r) => String(r.hour)} value={(r) => r.orders} /></div>
            {d.riders.length === 0 ? <Empty>No deliveries in this period.</Empty> : (
                <ResponsiveList testid="lse-analytics-riders" rows={d.riders} rowKey={(r) => r.rider_id}
                    columns={[
                        { key: "name", label: "Delivery partner", render: (r) => <span className="font-semibold">{r.name}</span> },
                        { key: "deliveries", label: "Deliveries" },
                        { key: "active", label: "Active hours", render: (r) => r.active_hours },
                        { key: "busy", label: "On an order", render: (r) => `${r.busy_hours} h` },
                        { key: "util", label: "Utilisation", render: (r) => `${r.utilisation_pct}%` },
                        { key: "rate", label: "Deliveries / hour", render: (r) => r.deliveries_per_active_hour },
                    ]}
                    card={(r) => (<div><p className="font-semibold">{r.name}</p><p className="text-xs text-muted-foreground">{r.deliveries} deliveries · {r.utilisation_pct}% utilised · {r.deliveries_per_active_hour}/h</p></div>)} />
            )}
        </div>
    );
}
